import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { starApi } from "@/lib/extensions-api";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";

interface Form {
  name: string;
  description: string;
  period_type: "daily" | "weekly" | "monthly" | "yearly";
  reward_strategy: "fixed" | "pool";
  own_point_target: number;
  combined_point_target: number;
  bonus_amount: string;
  contribution_per_entry: string;
}

export function StarOfferFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<Form>({
    defaultValues: { 
      period_type: "monthly",
      reward_strategy: "fixed"
    },
  });

  const create = useMutation({
    mutationFn: (v: Form) => starApi.admin.createOffer({
      ...v,
      own_point_target: Number(v.own_point_target),
      combined_point_target: Number(v.combined_point_target),
    }),
    onSuccess: () => {
      toast.success("Star Offer created.");
      qc.invalidateQueries({ queryKey: ["admin-star-offers"] });
      reset();
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Create Star Offer" size="lg">
      <form onSubmit={handleSubmit((v) => create.mutate(v))} className="space-y-4">
        <Input label="Offer Name" placeholder="Monthly Achievement Bonus"
                error={errors.name?.message}
                {...register("name", { required: "Name required" })} />
        <Input label="Description" {...register("description")} />

        <div className="grid grid-cols-2 gap-4">
          <Select label="Period Type" required
                   options={[
                     { value: "daily",   label: "Daily" },
                     { value: "weekly",  label: "Weekly" },
                     { value: "monthly", label: "Monthly" },
                     { value: "yearly",  label: "Yearly" },
                   ]}
                   {...register("period_type", { required: true })} />
          <Select label="Reward Strategy" required
                   options={[
                     { value: "fixed", label: "Fixed Bonus" },
                     { value: "pool",  label: "Global Pool (Royalty)" },
                   ]}
                   {...register("reward_strategy", { required: true })} />
        </div>

        {watch("reward_strategy") === "pool" ? (
          <div className="p-4 bg-brand-50 border border-brand-200 rounded-lg text-sm text-brand-800">
            <div className="font-semibold mb-2">Global Pool Distribution</div>
            <Input label="Contribution per new Club Entry (Tk)" type="number" step="0.01" required
                   hint="This amount will be added to the pool every time ANY user creates a Club Entry."
                   {...register("contribution_per_entry", { required: true })} />
          </div>
        ) : (
          <Input label="Fixed Bonus Amount" type="number" step="0.01" required
                 {...register("bonus_amount", { required: true })} />
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input label="Own Point Target" type="number" min="0" required
                  {...register("own_point_target", { required: true, valueAsNumber: true })} />
          <Input label="Combined Point Target" type="number" min="0" required
                  hint="Own + Team must reach this."
                  {...register("combined_point_target", { required: true, valueAsNumber: true })} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
          <Button type="submit" loading={create.isPending}>Create Offer</Button>
        </div>
      </form>
    </Modal>
  );
}